import AsyncStorage from '@react-native-async-storage/async-storage';
import { useNavigation, useRoute } from '@react-navigation/native';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, ScrollView, StyleSheet, View } from 'react-native';
import { ActivityIndicator, Button, Card, Checkbox, Divider, Text, TextInput } from 'react-native-paper';
import api from '../../services/api';
import { resolveItemPresentacion } from '../../utils/facturaGenerator';

const NuevaDevolucionScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { venta } = route.params || {};
  
  const [loading, setLoading] = useState(true);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [itemsSeleccionados, setItemsSeleccionados] = useState([]);
  const [ventaDetalles, setVentaDetalles] = useState([]);
  const [ventaCompleta, setVentaCompleta] = useState(null);
  
  // Cargar los detalles de la venta desde la API
  const cargarDetallesVenta = useCallback(async () => {
    if (!venta || !venta.id) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      console.log(`Cargando detalles de venta ID: ${venta.id}`);
      
      const detallesVenta = await api.getVenta(venta.id);
      console.log('Detalles de venta recibidos:', detallesVenta);
      
      if (detallesVenta && detallesVenta.items) {
        // Convertir cantidad y precio a unidades de presentación para que el
        // usuario edite en "Cajas" no en "unidades sueltas". Usa heurística que
        // detecta ventas legacy (valores ya en presentación) y no las convierte.
        const itemsConProductos = detallesVenta.items.map(item => {
          const { cantidadDisplay, precioDisplay, equivBase, precioBase, factor } =
            resolveItemPresentacion(item);
          return {
            ...item,
            producto: {
              id: item.producto_id,
              nombre: item.producto_nombre || 'Producto sin nombre',
              codigo: item.producto_codigo || `PROD-${item.producto_id}`
            },
            producto_codigo: item.producto_codigo || `PROD-${item.producto_id}`,
            unidad_id: item.unidad_id,
            unidad_nombre: item.unidad_nombre || 'Unidad',
            factor_conversion: factor,
            // Para enviar al backend: cantidad debe ir en unidades base.
            cantidad_base: equivBase,
            precio_unitario_base: precioBase,
            // Display y edición en presentación
            cantidad: cantidadDisplay,
            precio_unitario: precioDisplay,
          };
        });

        setVentaDetalles(itemsConProductos);
        setVentaCompleta(detallesVenta);
      } else {
        setVentaDetalles([]);
        Alert.alert('Error', 'No se pudieron cargar los detalles de la venta');
      }
    } catch (error) {
      console.error('Error al cargar detalles de venta:', error);
      Alert.alert('Error', 'No se pudieron cargar los detalles de la venta');
      setVentaDetalles([]);
    } finally {
      setLoading(false);
    }
  }, [venta?.id]);
  
  // Cargar datos al montar el componente
  useEffect(() => {
    cargarDetallesVenta();
  }, [cargarDetallesVenta]);

  const toggleItemSeleccion = (item) => {
    const index = itemsSeleccionados.findIndex(i => i.id === item.id);
    
    if (index >= 0) {
      // Si ya está seleccionado, lo quitamos
      const nuevosItems = [...itemsSeleccionados];
      nuevosItems.splice(index, 1);
      setItemsSeleccionados(nuevosItems);
    } else {
      // Si no está seleccionado, lo añadimos con la cantidad máxima
      const itemParaSeleccionar = {
        ...item,
        cantidadDevolucion: item.cantidad
      };
      console.log('=== SELECCIONANDO ITEM ===');
      console.log('Item original:', JSON.stringify(item, null, 2));
      console.log('Item para seleccionar:', JSON.stringify(itemParaSeleccionar, null, 2));
      
      setItemsSeleccionados([...itemsSeleccionados, itemParaSeleccionar]);
    }
  };

  const actualizarCantidadDevolucion = (id, cantidad) => {
    // Permitir que el campo esté vacío durante la edición
    if (cantidad === '') {
      setItemsSeleccionados(itemsSeleccionados.map(i => 
        i.id === id ? { ...i, cantidadDevolucion: '' } : i
      ));
      return;
    }
    
    // Convertir a número
    const cantidadNum = parseFloat(cantidad);
    if (isNaN(cantidadNum)) return;
    
    // Encontrar el item original para obtener la cantidad máxima
    const itemOriginal = ventaDetalles.find(i => i.id === id);
    if (!itemOriginal) return;
    
    // Asegurar que la cantidad no exceda la original
    const cantidadFinal = Math.min(cantidadNum, itemOriginal.cantidad);
    
    // Actualizar el estado
    setItemsSeleccionados(itemsSeleccionados.map(i => 
      i.id === id ? { ...i, cantidadDevolucion: cantidadFinal } : i
    ));
  };

  // Precio neto que el cliente realmente pagó por unidad de esa línea,
  // considerando descuento de línea y descuento global de la venta.
  // Coincide con el cálculo del backend para que UI y servidor concuerden.
  const netoUnitarioLinea = (item) => {
    const subtotalLinea = parseFloat(item.subtotal);
    const cantidadOriginal = parseFloat(item.cantidad);
    const ventaSubtotal = parseFloat(ventaCompleta?.subtotal) || 0;
    const ventaTotal = parseFloat(ventaCompleta?.total) || 0;
    if (!Number.isFinite(subtotalLinea) || cantidadOriginal <= 0) {
      return parseFloat(item.precio_unitario) || 0;
    }
    const factorGlobal = ventaSubtotal > 0 ? (ventaTotal / ventaSubtotal) : 1;
    return (subtotalLinea / cantidadOriginal) * factorGlobal;
  };

  const calcularTotalDevolucion = () => {
    return itemsSeleccionados.reduce((total, item) => {
      const cantidad = parseFloat(item.cantidadDevolucion) || 0;
      return total + cantidad * netoUnitarioLinea(item);
    }, 0);
  };

  const generateDevolucionHTML = (devolucionData) => {
    // Generar filas de productos. Mostramos el neto unitario real (con desc. aplicado),
    // no `precio_unitario` crudo, para que el monto del comprobante coincida con
    // lo que el cliente recibe de vuelta.
    const productosHTML = (devolucionData.items || []).map(item => {
      const netoUnit = netoUnitarioLinea(item);
      const cant = parseFloat(item.cantidadDevolucion || 0);
      return `
      <tr>
        <td>${item.producto?.nombre || item.producto_nombre || 'Producto sin nombre'}</td>
        <td>${cant} ${item.unidad_nombre || 'unidad'}</td>
        <td>L. ${netoUnit.toFixed(2)}</td>
        <td>L. ${(netoUnit * cant).toFixed(2)}</td>
      </tr>
    `;
    }).join('');
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Comprobante de Devolución</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 10px;
            width: 80mm;
            max-width: 80mm;
          }
          .header {
            text-align: center;
            margin-bottom: 10px;
            border-bottom: 1px dashed #000;
            padding-bottom: 10px;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 5px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 10px 0;
          }
          th, td {
            text-align: left;
            padding: 3px;
            font-size: 10px;
          }
          th {
            border-bottom: 1px solid #ddd;
          }
          .total {
            font-weight: bold;
            text-align: right;
            margin-top: 10px;
            border-top: 1px dashed #000;
            padding-top: 10px;
          }
          .footer {
            text-align: center;
            margin-top: 20px;
            font-size: 10px;
            border-top: 1px dashed #000;
            padding-top: 10px;
          }
          .motivo {
            margin: 10px 0;
            padding: 5px;
            background-color: #f5f5f5;
            border-radius: 5px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>DEVOLUCIÓN</h2>
          <p>Venta #${devolucionData.venta_id || 'N/A'}</p>
          <p>Fecha: ${devolucionData.fecha || 'N/A'}</p>
          <p>Hora: ${devolucionData.hora || ''}</p>
        </div>
        
        <div class="info-row">
          <span>Cliente:</span>
          <span>${devolucionData.cliente?.nombre || 'Cliente sin nombre'}</span>
        </div>
        
        <div class="info-row">
          <span>Devolución #:</span>
          <span>${devolucionData.id}</span>
        </div>
        
        <div class="motivo">
          <strong>Motivo:</strong> ${devolucionData.motivo}
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Producto</th>
              <th>Cant.</th>
              <th>Precio</th>
              <th>Subtotal</th>
            </tr>
          </thead>
          <tbody>
            ${productosHTML}
          </tbody>
        </table>
        
        <div class="total">
          Total Devuelto: L. ${parseFloat(devolucionData.total).toFixed(2)}
        </div>
        
        <div class="footer">
          <p>Gracias por su preferencia</p>
          <p>Sistema de Ventas v1.0</p>
        </div>
      </body>
      </html>
    `;
  };

  const generateAndSharePDF = async (devolucionData) => {
    try {
      setGeneratingPDF(true);
      
      // Generar HTML
      const html = generateDevolucionHTML(devolucionData);
      
      // Generar PDF
      const { uri } = await Print.printToFileAsync({
        html,
        base64: false
      });
      
      // Compartir PDF
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(uri, {
          mimeType: 'application/pdf',
          dialogTitle: `Comprobante de Devolución #${devolucionData.id}`,
          UTI: 'com.adobe.pdf'
        });
      setGeneratingPDF(false);
      return true;
    } else {
      Alert.alert('Error', 'La función de compartir no está disponible en este dispositivo');
      setGeneratingPDF(false);
      return false;
    }
  } catch (error) {
    console.error('Error al generar PDF:', error);
    Alert.alert('Error', 'No se pudo generar el PDF: ' + error.message);
    setGeneratingPDF(false);
    return false;
  }
};

  const handleSubmit = async () => {
    if (itemsSeleccionados.length === 0) {
      Alert.alert('Error', 'Debe seleccionar al menos un producto para devolver');
      return;
    }
    
    if (!motivo.trim()) {
      Alert.alert('Error', 'Debe ingresar un motivo para la devolución');
      return;
    }
    
    // Usar ventaCompleta para obtener información adicional si está disponible
    const clienteInfo = ventaCompleta?.cliente || { nombre: venta.cliente?.nombre || 'Cliente sin nombre' };
    
    try {
      setLoading(true);
      
      // Obtener el ID del usuario desde AsyncStorage
      let usuario_id = null;
      try {
        const userData = await AsyncStorage.getItem('user'); // Clave correcta según AuthContext.js
        if (userData) {
          const parsedUserData = JSON.parse(userData);
          usuario_id = parsedUserData.id;
          console.log('Usuario ID obtenido:', usuario_id);
        } else {
          console.warn('No se encontró información del usuario en AsyncStorage');
        }
      } catch (error) {
        console.error('Error al obtener usuario_id de AsyncStorage:', error);
      }
      
      if (!usuario_id) {
        Alert.alert('Error', 'No se pudo obtener la información del usuario. Por favor, inicie sesión nuevamente.');
        setLoading(false);
        return;
      }
      
      // Calcular el total de la devolución
      const totalDevolucion = calcularTotalDevolucion();
      
      // Preparar los datos de la devolución para la API
      console.log('=== ITEMS SELECCIONADOS ANTES DE ENVIAR AL BACKEND ===');
      console.log('itemsSeleccionados:', JSON.stringify(itemsSeleccionados, null, 2));
      
      const devolucionData = {
        venta_id: parseInt(venta?.id || 0),
        usuario_id: parseInt(usuario_id),
        motivo: motivo,
        total: totalDevolucion,
        // El backend almacena y restaura stock en unidades base; el usuario edita
        // en unidades de presentación. Convertimos cantidad × factor antes de enviar.
        items: itemsSeleccionados.map(item => {
          const factor = parseFloat(item.factor_conversion) > 0 ? parseFloat(item.factor_conversion) : 1;
          const cantidadPresentacion = parseFloat(item.cantidadDevolucion || 0);
          return {
            producto_id: parseInt(item.producto_id || (item.producto && item.producto.id) || 0),
            cantidad: cantidadPresentacion * factor,
            precio_unitario: parseFloat(item.precio_unitario_base ?? item.precio_unitario ?? 0),
            producto_nombre: item.producto_nombre || item.producto?.nombre || 'Producto sin nombre',
            producto_codigo: item.producto_codigo || item.producto?.codigo || `PROD-${item.producto_id || (item.producto && item.producto.id) || Date.now()}`,
          };
        })
      };
      
      console.log('=== DATOS FINALES A ENVIAR AL BACKEND ===');
      console.log('devolucionData:', JSON.stringify(devolucionData, null, 2));
      
      // Verificar que los campos requeridos estén presentes
      if (!devolucionData.venta_id || devolucionData.venta_id <= 0) {
        Alert.alert('Error', 'ID de venta inválido');
        return;
      }
    
      if (isNaN(devolucionData.total)) {
        devolucionData.total = 0; // Asegurarnos que total sea un número válido
      }
    
      // Verificar que todos los datos requeridos estén presentes
      console.log('Enviando devolución a la API:', JSON.stringify(devolucionData, null, 2));
      console.log('venta_id:', devolucionData.venta_id, 'total:', devolucionData.total, 'usuario_id:', devolucionData.usuario_id);
      console.log('Items seleccionados:', itemsSeleccionados.length);
      
      // Verificar que cada item tenga toda la información necesaria
      itemsSeleccionados.forEach((item, index) => {
        console.log(`Item ${index}: producto_id=${item.producto_id || item.producto?.id}, cantidad=${item.cantidadDevolucion}`);
        console.log(`  Unidad: id=${item.unidad_id}, nombre=${item.unidad_nombre || 'N/A'}`);
        console.log(`  Producto: nombre=${item.producto_nombre || item.producto?.nombre || 'N/A'}, codigo=${item.producto_codigo || item.producto?.codigo || 'N/A'}`);
      });
      
      // Enviar la devolución a la API
      const response = await api.createDevolucion(devolucionData);
      console.log('Respuesta de la API:', response);
      
      // La API se encarga de actualizar el stock y el estado de la venta
      // Ahora usamos el ID devuelto por la API
      const devolucionId = response.id || `DEV-${Date.now()}`;
      
      // Crear objeto completo para el PDF con los datos de la respuesta
      const devolucionCompleta = {
        id: devolucionId,
        venta_id: venta.id,
        fecha: new Date().toLocaleDateString(),
        hora: new Date().toLocaleTimeString(),
        cliente: clienteInfo,
        motivo: motivo,
        items: itemsSeleccionados,
        total: totalDevolucion,
        estado: 'completada'
      };
      
      // Generar y compartir el PDF
      const pdfGenerado = await generateAndSharePDF(devolucionCompleta);
      
      if (pdfGenerado) {
        Alert.alert(
          'Devolución Registrada',
          `La devolución #${devolucionId} ha sido registrada exitosamente. Los productos han sido devueltos al inventario y el estado de la venta ha sido actualizado.`,
          [
            { 
              text: 'Ver Devoluciones', 
              onPress: () => navigation.navigate('Devoluciones', { screen: 'DevolucionesList' }) 
            },
            { 
              text: 'OK', 
              onPress: () => navigation.goBack(),
              style: 'cancel' 
            }
          ]
        );
      }
    } catch (error) {
      console.error('Error al registrar devolución:', error);
      Alert.alert('Error', 'No se pudo registrar la devolución: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Si no hay venta seleccionada, mostrar pantalla de selección
  if (!venta || !venta.id) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorTitle}>Seleccione una venta</Text>
        <Text style={styles.errorText}>
          Para crear una devolución, primero debe seleccionar una venta desde el detalle de venta.
        </Text>
        <Button 
          mode="contained" 
          onPress={() => navigation.navigate('Ventas', { screen: 'VentasList' })}
          style={styles.errorButton}
        >
          Ir a Ventas
        </Button>
        <Button 
          mode="outlined" 
          onPress={() => navigation.goBack()}
          style={[styles.errorButton, { marginTop: 10 }]}
        >
          Volver
        </Button>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#007bff" />
          <Text style={styles.loadingText}>Cargando detalles de venta...</Text>
        </View>
      ) : generatingPDF ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#007bff" />
          <Text style={styles.loadingText}>Generando PDF...</Text>
        </View>
      ) : (
        <View>
          <Card style={styles.card}>
            <Card.Title title={`Devolución de Venta #${venta.id}`} />
            <Card.Content>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Fecha Venta:</Text>
                <Text style={styles.infoValue}>{venta.fecha || 'Fecha no disponible'}</Text>
              </View>
              
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Cliente:</Text>
                <Text style={styles.infoValue}>{venta.cliente?.nombre || 'Cliente sin nombre'}</Text>
              </View>
              
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Total Venta:</Text>
                <Text style={styles.infoValue}>L. {parseFloat(venta.total || 0).toFixed(2)}</Text>
              </View>
              
              <TextInput
                label="Motivo de la devolución"
                value={motivo}
                onChangeText={setMotivo}
                style={styles.input}
                multiline
                numberOfLines={3}
              />
            </Card.Content>
          </Card>
          
          <Card style={styles.card}>
            <Card.Title title="Seleccione los productos a devolver" />
            <Card.Content>
              {ventaDetalles.map((item, index) => {
                const factor = Number(item.factor_conversion) > 0 ? Number(item.factor_conversion) : 1;
                const tienePresentacion = factor > 1;
                const equivBase = (item.cantidad || 0) * factor;
                return (
                  <View key={item.id}>
                    <View style={styles.itemRow}>
                      <Checkbox
                        status={itemsSeleccionados.some(i => i.id === item.id) ? 'checked' : 'unchecked'}
                        onPress={() => toggleItemSeleccion(item)}
                      />

                      <View style={styles.itemInfo}>
                        <Text style={styles.itemName}>{item.producto?.nombre || item.producto_nombre || 'Producto sin nombre'}</Text>
                        <Text style={styles.itemDetails}>
                          Cantidad: {item.cantidad || 0} {item.unidad_nombre || 'unidad'}
                          {tienePresentacion ? ` (= ${equivBase} unidades)` : ''}
                          {'\n'}Precio: L. {parseFloat(item.precio_unitario || 0).toFixed(2)} / {item.unidad_nombre || 'unidad'}
                        </Text>
                      </View>
                    </View>

                    {itemsSeleccionados.some(i => i.id === item.id) && (
                      <View style={styles.cantidadContainer}>
                        <Text style={styles.cantidadLabel}>Cantidad a devolver ({item.unidad_nombre || 'unidad'}):</Text>
                        <TextInput
                          value={String(itemsSeleccionados.find(i => i.id === item.id).cantidadDevolucion)}
                          onChangeText={(text) => actualizarCantidadDevolucion(item.id, text)}
                          keyboardType="numeric"
                          style={styles.cantidadInput}
                          maxLength={5}
                        />
                      </View>
                    )}

                    {index < ventaDetalles.length - 1 && <Divider style={styles.divider} />}
                  </View>
                );
              })}
              
              {itemsSeleccionados.length > 0 && (
                <View style={styles.totalContainer}>
                  <Text style={styles.totalLabel}>Total a devolver:</Text>
                  <Text style={styles.totalValue}>
                    L. {calcularTotalDevolucion().toFixed(2)}
                  </Text>
                </View>
              )}
            </Card.Content>
          </Card>
          
          <Button
            mode="contained"
            onPress={handleSubmit}
            style={styles.submitButton}
            disabled={itemsSeleccionados.length === 0 || !motivo.trim()}
          >
            Registrar Devolución
          </Button>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    padding: 8,
  },
  card: {
    marginBottom: 16,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  infoLabel: {
    fontWeight: 'bold',
    color: '#555',
  },
  infoValue: {
    color: '#333',
  },
  input: {
    marginTop: 16,
    backgroundColor: '#fff',
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  itemInfo: {
    flex: 1,
    marginLeft: 8,
  },
  itemName: {
    fontSize: 16,
    fontWeight: '500',
  },
  itemDetails: {
    fontSize: 14,
    color: '#666',
  },
  cantidadContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 40,
    marginBottom: 8,
  },
  cantidadLabel: {
    fontSize: 14,
    marginRight: 8,
  },
  cantidadInput: {
    width: 80,
    height: 40,
    backgroundColor: '#fff',
  },
  divider: {
    marginVertical: 8,
  },
  totalContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: 16,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#ddd',
  },
  totalLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    marginRight: 8,
  },
  totalValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#007bff',
  },
  submitButton: {
    marginVertical: 16,
    paddingVertical: 8,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#555',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#f5f5f5',
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
    textAlign: 'center',
  },
  errorText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 24,
  },
  errorButton: {
    width: '100%',
    maxWidth: 200,
  },
});

export default NuevaDevolucionScreen;
